import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRfqQuotes, useSubmitFeedback, MAX_FEEDBACK_COMMENT } from "../rfqs/api.js";
import { Button, ButtonLink } from "../ui/Button.js";
import { Card } from "../ui/Card.js";

/**
 * The customer's feedback form, at /app/rfqs/:id/feedback and
 * /app/rfqs/:id/feedback/:preset.
 *
 * The `preset` segment is what the one-tap star links in the email carry
 * ("5", "3", "not-done"). It is a PATH segment rather than a query string
 * because ProtectedRoute keeps only `location.pathname` across the sign-in
 * bounce, and a tap from a phone inbox is nearly always signed out — a
 * `?rating=5` would be silently dropped and land the customer on a blank form
 * at exactly the moment their goodwill runs out.
 *
 * The preset PRE-FILLS, it never auto-submits. Submitting on someone's behalf
 * from a link a mail client may have prefetched would put ratings in the
 * record that no human ever chose.
 */
export function RfqFeedback() {
  const { id, preset } = useParams<{ id: string; preset?: string }>();
  const navigate = useNavigate();
  const { data, isPending } = useRfqQuotes(id);
  const submit = useSubmitFeedback(id!);

  const presetRating = preset && /^[1-5]$/.test(preset) ? Number(preset) : undefined;
  const presetNotDone = preset === "not-done";

  const existing = data?.feedback ?? null;
  const [workCompleted, setWorkCompleted] = useState<boolean | undefined>(
    presetNotDone ? false : presetRating !== undefined ? true : undefined,
  );
  const [rating, setRating] = useState<number | undefined>(presetRating);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);

  // Someone who already answered lands here again from an old email link.
  // Show what they said rather than a blank form pretending nothing happened.
  const answered = existing !== null && !done;

  const contractorName =
    data?.quotes.find((q) => q.quoteId === data.selection?.quoteId)?.contractorProfile
      ?.businessName ?? "the pro";

  if (isPending) return <p className="text-sm text-slate-600">Loading…</p>;

  if (done || answered) {
    return (
      <div className="mx-auto max-w-lg">
        <Card className="p-6 text-center">
          <h1 className="text-lg font-bold text-slate-900">Thanks — that's logged.</h1>
          <p className="mt-2 text-sm text-slate-600">
            {answered && !done
              ? "You've already rated this job. Thanks again."
              : "We read every one of these."}
          </p>
          <ButtonLink className="mt-4" to={`/app/rfqs/${id}`} variant="secondary">
            Back to your request
          </ButtonLink>
        </Card>
      </div>
    );
  }

  const canSubmit = workCompleted === false || (workCompleted === true && rating !== undefined);

  return (
    <div className="mx-auto max-w-lg">
      <Link to={`/app/rfqs/${id}`} className="text-sm text-brand-700 hover:underline">
        ← Back to your request
      </Link>
      <Card className="mt-3 p-6">
        <h1 className="text-lg font-bold text-slate-900">How did it go?</h1>

        <fieldset className="mt-5">
          <legend className="text-sm font-semibold text-slate-800">
            Did this work actually get done?
          </legend>
          <div className="mt-2 flex gap-2">
            {[
              { label: "Yes, it's done", value: true },
              { label: "No, it wasn't", value: false },
            ].map((o) => (
              <button
                key={String(o.value)}
                type="button"
                aria-pressed={workCompleted === o.value}
                onClick={() => {
                  setWorkCompleted(o.value);
                  if (!o.value) setRating(undefined);
                }}
                className={[
                  "min-h-[44px] flex-1 rounded-xl border px-3 text-sm font-semibold",
                  workCompleted === o.value
                    ? "border-brand-700 bg-brand-50 text-brand-900"
                    : "border-slate-300 bg-white text-slate-700",
                ].join(" ")}
              >
                {o.label}
              </button>
            ))}
          </div>
        </fieldset>

        {workCompleted === true && (
          <fieldset className="mt-5">
            <legend className="text-sm font-semibold text-slate-800">
              How did {contractorName} do?
            </legend>
            <div className="mt-2 flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-label={`${n} star${n === 1 ? "" : "s"}`}
                  aria-pressed={rating === n}
                  onClick={() => setRating(n)}
                  className={[
                    "min-h-[44px] flex-1 rounded-xl border text-lg font-semibold",
                    rating !== undefined && n <= rating
                      ? "border-brand-700 bg-brand-700 text-white"
                      : "border-slate-300 bg-white text-slate-400",
                  ].join(" ")}
                >
                  ★
                </button>
              ))}
            </div>
          </fieldset>
        )}

        {workCompleted !== undefined && (
          <label className="mt-5 block">
            <span className="text-sm font-semibold text-slate-800">
              Anything you'd like to add? (optional)
            </span>
            <textarea
              value={comment}
              maxLength={MAX_FEEDBACK_COMMENT}
              rows={4}
              onChange={(e) => setComment(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-300 p-3 text-sm"
              placeholder={
                workCompleted ? "What went well, or what didn't?" : "What happened?"
              }
            />
          </label>
        )}

        {workCompleted === false && (
          <p className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
            Thanks for telling us — someone from our team will follow up with you.
          </p>
        )}

        <div className="mt-5 flex gap-2">
          <Button
            disabled={!canSubmit}
            loading={submit.isPending}
            onClick={() =>
              submit.mutate(
                {
                  workCompleted: workCompleted!,
                  ...(rating !== undefined ? { rating } : {}),
                  ...(comment.trim() ? { comment: comment.trim() } : {}),
                },
                { onSuccess: () => setDone(true) },
              )
            }
          >
            Send
          </Button>
          <Button variant="ghost" onClick={() => void navigate(`/app/rfqs/${id}`)}>
            Not now
          </Button>
        </div>
        {submit.error && (
          <p className="mt-2 text-sm text-rose-700">
            Couldn&rsquo;t save that. Please try again.
          </p>
        )}
      </Card>
    </div>
  );
}
